import { RouteLocationNormalizedLoaded, RouteLocationRaw } from 'vue-router';

/** Where an in-portal help link leads. */
export type KbNavigationTarget =
    | { kind: 'article', id: string, fragment?: string }
    | { kind: 'section', id: string }
    | { kind: 'home' };

// The only shapes the backend rewrites a help-centre link to: `/help`, `/help/s/<id>`, and
// `/help/a/<id>` with an optional `#kb-...` fragment.
const PortalHelpPath = /^\/help(?:\/(a|s)\/([^/#?]+))?(?:#(.+))?$/;

/** Parses a backend-rewritten in-portal help href; anything else yields null. */
export function parsePortalHelpPath(href: string): KbNavigationTarget | null {
    const match = PortalHelpPath.exec(href);
    if (!match) {
        return null;
    }
    const [ , kind, id, fragment ] = match;
    if (kind === 'a') {
        return fragment ? { kind: 'article', id, fragment } : { kind: 'article', id };
    }
    if (kind === 's') {
        return { kind: 'section', id };
    }
    return { kind: 'home' };
}

/** The tenant in the current URL, or undefined for the tenant-free `/help/...` paths. The KB
 * routes' `rpId` is optional, so a missing one must stay undefined rather than becoming the
 * string "undefined" in a generated href. */
export function currentRpId(route: RouteLocationNormalizedLoaded): string | undefined {
    const { rpId } = route.params;
    return typeof rpId === 'string' && rpId ? rpId : undefined;
}

/** The named route for a target. Named routes in vue-router 4 don't inherit params from the
 * current route, so `rpId` has to be passed explicitly; it is left out entirely, not passed
 * as an explicit undefined, when there is no tenant. */
export function routeForTarget(target: KbNavigationTarget, rpId: string | undefined): RouteLocationRaw {
    const tenant = rpId ? { rpId } : {};
    switch (target.kind) {
        case 'article':
            return {
                name: 'kbArticle',
                params: { ...tenant, articleId: target.id },
                ...(target.fragment ? { hash: `#${target.fragment}` } : {}),
            };
        case 'section':
            return { name: 'kbSection', params: { ...tenant, sectionId: target.id } };
        default:
            return { name: 'kbHelp', params: { ...tenant } };
    }
}

/** The tenant-free location for a target, for every rendered `href` or `RouterLink :to`. Staff
 * copy these links to share with customers; the auth guard fills in the reader's own client when
 * they navigate, so the URL must not carry the sharer's. */
export function kbLocation(target: KbNavigationTarget): RouteLocationRaw {
    return routeForTarget(target, undefined);
}

/** Finds the in-portal target behind a click, from the attributes `KbArticleContent`
 * stamps on every link it resolved. An anchor still carrying the backend's external
 * marker is left to the browser. */
export function resolveInternalTarget(element: HTMLElement): KbNavigationTarget | null {
    const anchor = element.closest('a');
    if (!anchor || anchor.getAttribute('data-kb-external') === 'true') {
        return null;
    }
    const kind = anchor.getAttribute('data-kb-kind');
    const id = anchor.getAttribute('data-kb-id') || '';
    const fragment = anchor.getAttribute('data-kb-fragment') || undefined;
    if (kind === 'article' && id) {
        return fragment ? { kind, id, fragment } : { kind, id };
    }
    if (kind === 'section' && id) {
        return { kind, id };
    }
    if (kind === 'home') {
        return { kind };
    }
    return null;
}
